import {
  RadioGroup,
  RadioGroupItem,
} from "@volca/ui/components/ui/radio-group";
import { cn } from "@volca/ui/lib/utils";
import type { PersonAccessMode } from "@/payroll/workbook-access/person-access-form";

const MODES: { value: PersonAccessMode; label: string; hint: string }[] = [
  {
    value: "none",
    label: "No access",
    hint: "Can't open the payroll workbook",
  },
  {
    value: "full",
    label: "Whole workbook",
    hint: "Every tab and every technician",
  },
  {
    value: "areas",
    label: "Specific sheets",
    hint: "Only the sheets you pick",
  },
];

const isPersonAccessMode = (value: unknown): value is PersonAccessMode =>
  MODES.some((mode) => mode.value === value);

interface AccessModeOptionsProps {
  value: PersonAccessMode;
  allowed?: PersonAccessMode[];
  onChange: (mode: PersonAccessMode) => void;
}

export const AccessModeOptions = ({
  value,
  allowed,
  onChange,
}: AccessModeOptionsProps) => (
  <RadioGroup
    value={value}
    onValueChange={(mode) => isPersonAccessMode(mode) && onChange(mode)}
    aria-label="Workbook access"
    className="w-full gap-0 overflow-hidden rounded-xl border border-line"
  >
    {MODES.map((mode) => {
      const disabled = allowed != null && !allowed.includes(mode.value);
      return (
        <label
          key={mode.value}
          htmlFor={`mode-${mode.value}`}
          className={cn(
            "flex items-start gap-3 border-b border-line px-4 py-3 last:border-b-0",
            {
              "bg-selected text-brand hover:bg-selected-hovered":
                value === mode.value,
              "cursor-pointer": !disabled,
              "cursor-not-allowed": disabled,
            },
          )}
        >
          <RadioGroupItem
            id={`mode-${mode.value}`}
            value={mode.value}
            disabled={disabled}
            className="mt-0.5"
          />
          <span className="flex flex-col">
            <span
              className={cn("text-sm font-semibold", {
                "text-ink": !disabled && value !== mode.value,
                "text-brand": !disabled && value === mode.value,
                "text-subtle": disabled,
              })}
            >
              {mode.label}
            </span>
            <span
              className={cn("text-xs text-subtle", {
                "opacity-70": disabled,
              })}
            >
              {mode.hint}
            </span>
          </span>
        </label>
      );
    })}
  </RadioGroup>
);
